'use client';

import * as React from 'react';
import { Button, Modal } from '@heroui/react';
import { DropZone } from '@heroui-pro/react';
import { IconCircleCheck, IconCircleX, IconUpload } from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import {
  ensureAnonymousAuth,
  uploadFileDirect,
} from '@hs/shared';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { MEDIA_TYPE_ACCEPT } from './constants';
import type { AssetMediaType } from './types';

const CLOUDBASE_ENV_ID = 'his-1g5d6vlz6368064f';

type DropItem = { kind: string; getFile?: () => Promise<File> };

type UploadRowStatus = 'uploading' | 'complete' | 'failed';

type FileFormatColor = 'blue' | 'gray' | 'green' | 'orange' | 'purple' | 'red';

type UploadRow = {
  id: string;
  file: File;
  name: string;
  size: number;
  status: UploadRowStatus;
  progress: number;
  errorMessage?: string;
};

export type UploadAssetModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mediaType: AssetMediaType;
  /** 上传成功并完成落库后（资产始终落「我的」） */
  onUploaded?: () => void;
};

const MEDIA_TYPE_DROP_HINT: Record<AssetMediaType, string> = {
  image: '支持图片格式，可多选。',
  audio: '支持音频格式，可多选。',
  video: '支持视频格式，可多选。',
  document: '支持文档格式，可多选。',
};

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1) : '';
}

function getFormatColor(ext: string): FileFormatColor {
  const map: Record<string, FileFormatColor> = {
    csv: 'green',
    doc: 'blue',
    docx: 'blue',
    jpeg: 'blue',
    jpg: 'blue',
    json: 'orange',
    mp3: 'purple',
    mp4: 'purple',
    pdf: 'red',
    png: 'green',
    svg: 'green',
    txt: 'gray',
    wav: 'purple',
    webm: 'purple',
    xlsx: 'green',
    zip: 'orange',
  };
  return map[ext.toLowerCase()] ?? 'gray';
}

let uploadRowIdSeq = 0;

function createUploadRowId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    try {
      return c.randomUUID();
    } catch {
      /* secure context 限制 */
    }
  }
  uploadRowIdSeq += 1;
  return `u-${Date.now()}-${uploadRowIdSeq}-${Math.random().toString(36).slice(2, 11)}`;
}

export function UploadAssetModal({
  open,
  onOpenChange,
  mediaType,
  onUploaded,
}: UploadAssetModalProps) {
  const [rows, setRows] = React.useState<UploadRow[]>([]);
  const cancelRef = React.useRef(new Map<string, { cancelled: boolean }>());

  const authorizeUpload = trpc.assets.authorizeUpload.useMutation();
  const createMedia = trpc.assets.create.useMutation();
  const utils = trpc.useUtils();

  React.useEffect(() => {
    if (open) {
      setRows([]);
      cancelRef.current.clear();
    }
  }, [open]);

  const updateRow = React.useCallback((id: string, patch: Partial<UploadRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const runOne = React.useCallback(
    async (rowId: string, file: File) => {
      const cancelEntry = { cancelled: false };
      cancelRef.current.set(rowId, cancelEntry);

      try {
        const { objectKey } = await authorizeUpload.mutateAsync({
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
        });
        if (cancelEntry.cancelled) return;

        await ensureAnonymousAuth(CLOUDBASE_ENV_ID);
        const result = await uploadFileDirect(
          { envId: CLOUDBASE_ENV_ID },
          file,
          {
            path: objectKey,
            onProgress: (p) => {
              if (cancelEntry.cancelled) return;
              updateRow(rowId, { progress: p.percent });
            },
          },
        );
        if (cancelEntry.cancelled) return;

        await createMedia.mutateAsync({
          name: file.name,
          fileId: result.fileId,
          fileSize: file.size,
          mimeType: file.type || 'application/octet-stream',
          type: mediaType,
          isOwner: true,
        });

        if (cancelEntry.cancelled) return;

        updateRow(rowId, { status: 'complete', progress: 100 });
        void utils.assets.listMy.invalidate({ type: mediaType });
        onUploaded?.();
      } catch (err) {
        if (cancelEntry.cancelled) return;
        const message = err instanceof Error ? err.message : '上传失败';
        updateRow(rowId, {
          status: 'failed',
          progress: 0,
          errorMessage: message,
        });
      } finally {
        cancelRef.current.delete(rowId);
      }
    },
    [authorizeUpload, createMedia, mediaType, onUploaded, updateRow, utils.assets.listMy],
  );

  const enqueueFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const newRows: UploadRow[] = files.map((f) => ({
        id: createUploadRowId(),
        file: f,
        name: f.name,
        size: f.size,
        status: 'uploading' as const,
        progress: 0,
      }));
      setRows((prev) => [...newRows, ...prev]);
      for (const r of newRows) {
        void runOne(r.id, r.file);
      }
    },
    [runOne],
  );

  const handleSelect = React.useCallback(
    (fileList: FileList) => {
      enqueueFiles(Array.from(fileList));
    },
    [enqueueFiles],
  );

  const handleDrop = React.useCallback(
    async (e: { items: DropItem[] }) => {
      const dropped: File[] = [];
      for (const item of e.items) {
        if (item.kind === 'file' && item.getFile) {
          dropped.push(await item.getFile());
        }
      }
      enqueueFiles(dropped);
    },
    [enqueueFiles],
  );

  const handleRemove = React.useCallback((id: string) => {
    const entry = cancelRef.current.get(id);
    if (entry) entry.cancelled = true;
    cancelRef.current.delete(id);
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const handleRetry = React.useCallback(
    (id: string) => {
      setRows((prev) => {
        const row = prev.find((r) => r.id === id);
        if (!row) return prev;
        void runOne(id, row.file);
        return prev.map((r) =>
          r.id === id
            ? { ...r, status: 'uploading' as const, progress: 0, errorMessage: undefined }
            : r,
        );
      });
    },
    [runOne],
  );

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        cancelRef.current.forEach((e) => {
          e.cancelled = true;
        });
        cancelRef.current.clear();
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  const accept = MEDIA_TYPE_ACCEPT[mediaType];

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside">
          <Modal.Dialog className="z-[1300] flex max-h-[85vh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0">
            <Modal.Header className="shrink-0 border-b px-6 py-4">
              <Modal.Heading>上传文件</Modal.Heading>
            </Modal.Header>

            <Modal.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-6">
              <DropZone className="w-full min-w-0">
                <DropZone.Area
                  onDrop={handleDrop as React.ComponentProps<typeof DropZone.Area>['onDrop']}
                >
                  <DropZone.Icon>
                    <IconUpload className="size-8" stroke={1.25} />
                  </DropZone.Icon>
                  <DropZone.Label>上传文件</DropZone.Label>
                  <DropZone.Description>
                    也可把文件拖到虚线框内实现上传。{MEDIA_TYPE_DROP_HINT[mediaType]}
                  </DropZone.Description>
                  <DropZone.Trigger>
                    选择文件
                  </DropZone.Trigger>
                </DropZone.Area>
                <DropZone.Input accept={accept} multiple onSelect={handleSelect} />

                {rows.length > 0 ? (
                  <DropZone.FileList>
                    {rows.map((row) => {
                      const ext = getExtension(row.name).toUpperCase() || 'FILE';

                      return (
                        <DropZone.FileItem key={row.id} status={row.status}>
                          <DropZone.FileFormatIcon
                            color={getFormatColor(getExtension(row.name))}
                            format={ext}
                          />
                          <DropZone.FileInfo>
                            <DropZone.FileName>{row.name}</DropZone.FileName>
                            <DropZone.FileMeta>
                              {formatFileSize(row.size)}
                              {row.status === 'uploading' ? ' | 上传中…' : null}
                              {row.status === 'complete' ? (
                                <span className="inline-flex items-center gap-1">
                                  {' | '}
                                  <IconCircleCheck className="text-success inline size-3" stroke={2} />
                                  完成
                                </span>
                              ) : null}
                              {row.status === 'failed' ? (
                                <span className="inline-flex items-center gap-1">
                                  {' | '}
                                  <IconCircleX className="text-danger inline size-3" stroke={2} />
                                  <span className="text-danger">
                                    {row.errorMessage ?? '上传失败'}
                                  </span>
                                </span>
                              ) : null}
                            </DropZone.FileMeta>
                            {row.status === 'uploading' || row.status === 'complete' ? (
                              <DropZone.FileProgress value={row.progress}>
                                <DropZone.FileProgressTrack>
                                  <DropZone.FileProgressFill />
                                </DropZone.FileProgressTrack>
                              </DropZone.FileProgress>
                            ) : null}
                            {row.status === 'failed' ? (
                              <Button
                                className="-ml-1 mt-2"
                                size="sm"
                                variant="danger-soft"
                                onPress={() => handleRetry(row.id)}
                              >
                                重试
                              </Button>
                            ) : null}
                          </DropZone.FileInfo>
                          <DropZone.FileRemoveTrigger
                            aria-label={`移除 ${row.name}`}
                            onPress={() => handleRemove(row.id)}
                          />
                        </DropZone.FileItem>
                      );
                    })}
                  </DropZone.FileList>
                ) : null}
              </DropZone>
            </Modal.Body>

            <Modal.Footer className="shrink-0 justify-end border-t px-6 py-4">
              <Button variant="primary" onPress={() => handleModalOpenChange(false)}>
                关闭
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
